import { useMemo, useState } from "react";

import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useClose } from "@headlessui/react";
import { twJoin } from "tailwind-merge";

import { DecisionEnvironment } from "src/api/types";
import { CustomPopover } from "src/base-components/Popover";
import { ResourceSearchPane } from "src/datasets/components/ResourceSearchPane";
import { IconButton } from "src/design-system/Icon";
import { FIELD_BORDER } from "src/design-system/utils/fieldStyles";
import { LinkedEntityPill } from "src/entities/entityView/LinkedEntityPill";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { useEntities, useEntity, useEntitySchemas } from "src/entities/queries";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useWorkspaceContext } from "src/router/routerContextHooks";

type SearchableEntityRefInputProps = {
  entityType: string;
  value: string;
  onChange: (id: string) => void;
  errored: boolean;
};

export const SearchableEntityRefInput: React.FC<
  SearchableEntityRefInputProps
> = ({ entityType, value, onChange, errored }) => {
  const [pageEnv] = useEnvironment();
  const env = pageEnv as DecisionEnvironment;
  const [searchQuery, setSearchQuery] = useState("");

  const featureGates = useWorkspaceFeatureGates();
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
    options: { enabled: featureGates.entitiesEnabled },
  });

  const schema = findSchema(entityType, entitySchemas);
  const primaryProperty = schema?._primary_property;
  const primaryPropertyDisplayName = primaryProperty
    ? (schema?.properties[primaryProperty]?._display_name ?? primaryProperty)
    : undefined;
  const entityIcon = schema ? getSchemaIcon(schema) : undefined;

  const userIsSearching = searchQuery.length > 0;

  const { data: entitiesByProp, isLoading: isLoadingByProp } = useEntities({
    schema: entityType,
    env,
    filters:
      userIsSearching && primaryProperty
        ? {
            [`filter[${primaryProperty}][str_contains]`]: searchQuery,
          }
        : {},
    limit: 20,
  });

  const { data: entityById, isLoading: isLoadingById } = useEntity({
    schema: entityType,
    env,
    entityId: userIsSearching ? searchQuery : undefined,
    enabled: userIsSearching,
  });

  const { data: selectedEntity } = useEntity({
    schema: entityType,
    env,
    entityId: value || undefined,
  });

  // Resolve display name from dedicated query or search results already in memory
  const pillDisplayName = useMemo(() => {
    if (!primaryProperty) return null;
    const entity =
      selectedEntity ??
      (entityById?._id === value ? entityById : undefined) ??
      entitiesByProp?.pages[0]?.entities?.find((e) => e._id === value);
    return (entity?.properties[primaryProperty] as string) || null;
  }, [selectedEntity, primaryProperty, value, entityById, entitiesByProp]);

  const items = useMemo(() => {
    const result: { label: string; key: string; isFetching: boolean }[] = [];
    const seenIds = new Set<string>();

    if (entityById) {
      seenIds.add(entityById._id);
      const ppValue = primaryProperty
        ? (entityById.properties[primaryProperty] as string)
        : undefined;
      result.push({
        label: ppValue ? `${ppValue} (${entityById._id})` : entityById._id,
        key: entityById._id,
        isFetching: false,
      });
    }

    const foundEntities = entitiesByProp?.pages[0]?.entities ?? [];
    for (const entity of foundEntities) {
      if (seenIds.has(entity._id)) continue;
      seenIds.add(entity._id);
      const ppValue = primaryProperty
        ? (entity.properties[primaryProperty] as string)
        : undefined;
      result.push({
        label: ppValue ? `${ppValue} (${entity._id})` : entity._id,
        key: entity._id,
        isFetching: false,
      });
    }

    return result;
  }, [entityById, entitiesByProp, primaryProperty]);

  const isLoading = isLoadingByProp || (userIsSearching && isLoadingById);
  const displayName =
    schema?._display_name_plural ??
    schema?._display_name_singular ??
    "Entities";

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <CustomPopover
      button={
        <div
          className={twJoin(
            "flex h-8 w-full items-center rounded-lg border bg-white px-3 text-left font-inter-normal-12px focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
            !errored && FIELD_BORDER.default,
            errored && FIELD_BORDER.errored,
          )}
          data-loc="entity-ref-value"
          tabIndex={0}
        >
          {value ? (
            <div className="flex w-full items-center gap-1">
              <span onClick={(e) => e.stopPropagation()}>
                <LinkedEntityPill
                  entityIcon={entityIcon}
                  environment={env}
                  fullWidth={false}
                  id={value}
                  name={pillDisplayName || value}
                  schemaId={entityType}
                />
              </span>
              <div className="ml-auto">
                <IconButton icon={faTimes} size="xs" onClick={handleClear} />
              </div>
            </div>
          ) : (
            <span className="text-gray-500">Search Entity</span>
          )}
        </div>
      }
      placement="bottom-start"
      wrapperClassName="w-full"
    >
      <EntitySearchPaneContent
        displayName={displayName}
        env={env}
        isLoading={isLoading}
        items={items}
        primaryPropertyDisplayName={primaryPropertyDisplayName}
        searchQuery={searchQuery}
        onChange={onChange}
        onSearchQueryChange={setSearchQuery}
      />
    </CustomPopover>
  );
};

const EntitySearchPaneContent: React.FC<{
  env: DecisionEnvironment;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  isLoading: boolean;
  items: { label: string; key: string; isFetching: boolean }[];
  displayName: string;
  primaryPropertyDisplayName: string | undefined;
  onChange: (id: string) => void;
}> = ({
  env,
  searchQuery,
  onSearchQueryChange,
  isLoading,
  items,
  displayName,
  primaryPropertyDisplayName,
  onChange,
}) => {
  const close = useClose();

  return (
    <div className="w-96">
      <ResourceSearchPane
        filters={{
          environment: env,
          searchQuery,
        }}
        hideEnvironmentFilter
        isLoading={isLoading}
        items={items}
        resourceName={displayName}
        searchPlaceholder={
          primaryPropertyDisplayName
            ? `Search by ID or ${primaryPropertyDisplayName}`
            : "Search by ID"
        }
        onChangeFilters={(filters) => {
          onSearchQueryChange(filters.searchQuery);
        }}
        onSelect={(item) => {
          onChange(item.key);
          close();
        }}
      />
    </div>
  );
};
